import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, Check, Upload, Loader2 } from "lucide-react";
import SiteShell from "../components/SiteShell";
import HoverChat from "../components/HoverChat";
import Action from "../components/Action";
import { submitAccessRequest, trackAction, fetchPartnerMomentum } from "../lib/api";
import { Field, inputStyle, primaryButtonStyle } from "./audit/StepEnergy";

/**
 * `/partner` — the public SPV Data Room gateway.
 *
 * Sources of truth:
 *   - prompts/03_spv_data_room.md
 *   - reference/funding_attraction_strategy.md (inbound posture)
 *   - reference/extracts/GreenGoldEnergy_InvestorPitch_TEXT.txt (facts)
 *
 * Non-negotiable: this is an *allocation of an exclusive asset*, not a
 * fundraise. Never "please invest in us." SPV / BOOT only — parent equity
 * off-limits.
 */

const METRICS = [
  { label: "Allocation ticket", value: "₹850 Cr", note: "Milestone-gated tranches" },
  { label: "Plant capacity", value: "720 MWh/yr", note: "LFP now · sodium-ion next" },
  { label: "Site", value: "SIPCOT, Coimbatore", note: "Tamil Nadu · SEZ-adjacent" },
  { label: "Structure", value: "BOOT + co-owned SPV", note: "Parent held privately" },
  { label: "Cadence", value: "Tranche-milestone gated", note: "Cell line → shipments → dispatch" },
];

const TICKET_BANDS = [
  "₹50-100 Cr",
  "₹100-250 Cr",
  "₹250-500 Cr",
  "₹500 Cr+",
  "Advisory / non-capital",
];

export default function Partner() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [momentum, setMomentum] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchPartnerMomentum()
      .then((m) => { if (!cancelled) setMomentum(m); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <SiteShell>
      <section
        style={{
          maxWidth: 1080,
          margin: "0 auto",
          padding:
            "clamp(56px, 8vw, 100px) clamp(20px, 5vw, 60px) clamp(40px, 6vw, 80px)",
        }}
        data-testid="partner-page"
      >
        <div className="gg-eyebrow" style={{ marginBottom: 12 }}>
          SPV Data Room · Funder gateway
        </div>
        <h1
          className="gg-hero-title"
          style={{ marginBottom: 20, fontSize: "clamp(32px, 5vw, 60px)", maxWidth: "22ch" }}
          data-testid="partner-headline"
        >
          We are allocating co-ownership of a strategic plant to one partner.
        </h1>
        <p
          style={{
            color: "var(--text-dim)",
            fontSize: "clamp(15.5px, 1.6vw, 18px)",
            lineHeight: 1.7,
            maxWidth: 700,
          }}
          data-testid="partner-lede"
        >
          A single 720 MWh/yr <HoverChat term="lfp">LFP</HoverChat> gigafactory
          in Coimbatore, held inside a{" "}
          <HoverChat term="boot-spv">BOOT + co-owned SPV</HoverChat>. Not a
          capital raise into the parent company — a titled seat at the plant,
          with transparent{" "}
          <HoverChat term="milestone-tranches">milestone tranches</HoverChat>{" "}
          and a defined exit path. Request access below.
        </p>

        {/* Metrics band */}
        <div
          data-testid="partner-metrics-band"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
            gap: 14,
            marginTop: 36,
          }}
        >
          {METRICS.map((m) => (
            <div
              key={m.label}
              className="gg-glass"
              data-testid={`partner-metric-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
              style={{ padding: 16, display: "flex", flexDirection: "column", gap: 6 }}
            >
              <div
                style={{
                  fontSize: 10.5,
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  color: "var(--text-mute)",
                }}
              >
                {m.label}
              </div>
              <div
                style={{
                  fontFamily: "Fraunces, Georgia, serif",
                  fontSize: 20,
                  lineHeight: 1.2,
                  color: "var(--paper)",
                }}
              >
                {m.value}
              </div>
              <div style={{ color: "var(--text-dim)", fontSize: 12.5, lineHeight: 1.5 }}>
                {m.note}
              </div>
            </div>
          ))}
        </div>

        {/* Three paragraphs */}
        <div style={{ marginTop: 46, display: "flex", flexDirection: "column", gap: 26, maxWidth: 780 }}>
          <p style={{ color: "var(--text-cloud)", fontSize: 16, lineHeight: 1.75 }} data-testid="partner-para-positioning">
            <strong style={{ color: "var(--gold-soft)" }}>Positioning.</strong>{" "}
            A non-Chinese, Indo-Pacific supply node — the China+1 axis Indian
            and allied capital has been asking for. Tamil Nadu state support is
            in process (SEZ and land instruments filed), and an{" "}
            <HoverChat term="lfp">IIT</HoverChat> research bench of three
            professors and four engineers is committed to the plant, alongside
            a 15-person in-house R&D group and a 60+ execution team.
          </p>
          <p style={{ color: "var(--text-cloud)", fontSize: 16, lineHeight: 1.75 }} data-testid="partner-para-structure">
            <strong style={{ color: "var(--gold-soft)" }}>Structure.</strong>{" "}
            The plant is held in a separate legal vehicle —{" "}
            <HoverChat term="boot-spv">Build-Operate-Own-Transfer</HoverChat>,
            co-owned by Green Gold Energy and one partner. Capital deploys in
            tranches keyed to construction milestones: cell line commissioned,
            first shipments, first MWh dispatched under{" "}
            <HoverChat term="gcpp">Group Captive</HoverChat> and{" "}
            <HoverChat term="cbam">CBAM</HoverChat>-grade offtake. The parent
            company stays privately held; only the SPV is on the table.
          </p>
          <p style={{ color: "var(--text-cloud)", fontSize: 16, lineHeight: 1.75 }} data-testid="partner-para-ask">
            <strong style={{ color: "var(--gold-soft)" }}>The ask.</strong>{" "}
            One strategic plant. One co-owning partner. If your fund or family
            office is aligned with a long-hold, milestone-gated infrastructure
            allocation in the Indian energy transition, this is a conversation
            worth starting.
          </p>
        </div>

        {/* Single action */}
        <p
          style={{
            marginTop: 42,
            color: "var(--text-cloud)",
            fontSize: 17,
            lineHeight: 1.75,
            maxWidth: 780,
          }}
          data-testid="partner-cta-line"
        >
          When you're ready,{" "}
          <Action
            route="request-data-room"
            source="partner-page"
            onClick={() => {
              setDrawerOpen(true);
              trackAction({ route: "request-data-room", source: "partner-page", sourceState: "gateway" });
            }}
          >
            request access to the data room
          </Action>
          . We review each request within 3 business days and issue a secure
          link to the room by email.
        </p>

        {/* Anonymised momentum ticker — hidden if either count is 0. */}
        {momentum && (momentum.enquiries_last_30d > 0 || momentum.active_partnership_rfps > 0) && (
          <p
            data-testid="partner-momentum-ticker"
            style={{
              marginTop: 14,
              color: "var(--text-mute)",
              fontSize: 12.5,
              letterSpacing: "0.04em",
            }}
          >
            {momentum.enquiries_last_30d > 0 && (
              <span data-testid="partner-momentum-enquiries">
                {momentum.enquiries_last_30d} partnership {momentum.enquiries_last_30d === 1 ? "enquiry" : "enquiries"} in the last 30 days
              </span>
            )}
            {momentum.enquiries_last_30d > 0 && momentum.active_partnership_rfps > 0 && (
              <span style={{ margin: "0 10px", color: "var(--text-mute)" }}>·</span>
            )}
            {momentum.active_partnership_rfps > 0 && (
              <span data-testid="partner-momentum-rfps">
                {momentum.active_partnership_rfps} active in review
              </span>
            )}
          </p>
        )}

        {drawerOpen && (
          <AccessRequestDrawer onClose={() => setDrawerOpen(false)} />
        )}
      </section>
    </SiteShell>
  );
}

// ============ Access-request drawer (in-page, no route change) =============
function AccessRequestDrawer({ onClose }) {
  const [form, setForm] = useState({
    name: "",
    org: "",
    role: "",
    email: "",
    country: "",
    ticket_band: TICKET_BANDS[1],
    intent: "",
  });
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(null);

  const set = (patch) => setForm({ ...form, ...patch });

  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email);
  const intentLen = form.intent.trim().length;
  const intentOk = intentLen >= 50 && intentLen <= 500;
  const canSubmit =
    form.name.trim() &&
    form.org.trim() &&
    form.role.trim() &&
    emailOk &&
    form.country.trim() &&
    intentOk &&
    !busy;

  const onFile = (e) => {
    const f = e.target.files?.[0];
    setFileError("");
    if (!f) {
      setFile(null);
      return;
    }
    const ext = f.name.slice(f.name.lastIndexOf(".")).toLowerCase();
    if (![".pdf", ".docx"].includes(ext)) {
      setFileError("Only .pdf or .docx accepted for the intro deck.");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setFileError(`"${f.name}" exceeds the 10 MB limit.`);
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErr("");
    try {
      const data = await submitAccessRequest({
        fields: form,
        files: file ? [file] : [],
      });
      setOk(data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex?.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  if (ok) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="access-request-confirmation"
        style={{
          marginTop: 32,
          padding: 26,
          borderRadius: "var(--radius-lg)",
          background: "linear-gradient(180deg, rgba(31,170,89,0.13), rgba(31,170,89,0.02))",
          border: "1px solid rgba(31,170,89,0.4)",
        }}
      >
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
          <Check size={18} color="var(--green-bright)" />
          <span className="gg-eyebrow" style={{ color: "var(--green-bright)", margin: 0 }}>
            Request received
          </span>
        </div>
        <h3 className="gg-h2">{ok.message}</h3>
        <p style={{ marginTop: 12, fontFamily: "Fraunces, Georgia, serif", fontSize: 20, color: "var(--gold-soft)" }}>
          Reference: <span data-testid="access-ref-id">{ok.ref_id}</span>
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      transition={{ duration: 0.35 }}
      data-testid="access-request-drawer"
      style={{ marginTop: 32, overflow: "hidden" }}
    >
      <div
        style={{
          padding: "24px 26px 28px",
          borderRadius: "var(--radius-lg)",
          background: "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.01))",
          border: "1px solid var(--line-strong)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 18 }}>
          <div>
            <div className="gg-eyebrow" style={{ marginBottom: 4 }}>Access request</div>
            <h3 className="gg-h2">Introduce yourself.</h3>
            <p style={{ color: "var(--text-dim)", fontSize: 14, marginTop: 6, lineHeight: 1.6, maxWidth: 620 }}>
              A short one-liner about what you're looking for is more useful
              than any pitch deck. We read every one.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close access request"
            data-testid="access-close"
            style={{
              background: "transparent", border: "1px solid var(--line)", borderRadius: 8,
              width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center",
              color: "var(--text-dim)", cursor: "pointer", flexShrink: 0,
            }}
          >
            <X size={14} />
          </button>
        </div>

        <form onSubmit={submit}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
            <Field label="Full name*"><input type="text" required value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="access-input-name" style={inputStyle} /></Field>
            <Field label="Organisation*"><input type="text" required value={form.org} onChange={(e) => set({ org: e.target.value })} data-testid="access-input-org" style={inputStyle} /></Field>
            <Field label="Role / title*"><input type="text" required value={form.role} onChange={(e) => set({ role: e.target.value })} data-testid="access-input-role" style={inputStyle} /></Field>
            <Field label="Email*">
              <input
                type="email" required value={form.email} onChange={(e) => set({ email: e.target.value })}
                data-testid="access-input-email"
                style={{ ...inputStyle, borderColor: form.email && !emailOk ? "rgba(255,120,120,0.5)" : "var(--line)" }}
              />
            </Field>
            <Field label="Country*"><input type="text" required value={form.country} onChange={(e) => set({ country: e.target.value })} data-testid="access-input-country" style={inputStyle} /></Field>
            <Field label="Ticket size band*">
              <select value={form.ticket_band} onChange={(e) => set({ ticket_band: e.target.value })} data-testid="access-input-ticket-band" style={inputStyle}>
                {TICKET_BANDS.map((b) => <option key={b} value={b} style={{ background: "var(--night-2)" }}>{b}</option>)}
              </select>
            </Field>
          </div>

          <div style={{ marginTop: 16 }}>
            <Field label={`One-line context* (${intentLen}/500 characters, min 50)`}>
              <textarea
                required rows={4} value={form.intent} onChange={(e) => set({ intent: e.target.value })}
                data-testid="access-input-intent"
                style={{ ...inputStyle, resize: "vertical", minHeight: 90, fontFamily: "inherit",
                  borderColor: form.intent && !intentOk ? "rgba(255,120,120,0.5)" : "var(--line)" }}
                placeholder="What you're looking to allocate, your hold horizon, why this asset — in your own words."
              />
            </Field>
          </div>

          <div style={{ marginTop: 18 }}>
            <label htmlFor="access-file-input" style={{
              display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 14px",
              borderRadius: 10, border: "1px dashed var(--line-strong)", cursor: "pointer",
              color: "var(--text-dim)", fontSize: 14,
            }}>
              <Upload size={14} /> Attach one intro deck or firm profile (.pdf / .docx ≤ 10 MB, optional)
            </label>
            <input id="access-file-input" type="file" accept=".pdf,.docx" onChange={onFile} data-testid="access-file-input" style={{ display: "none" }} />
            {file && <div data-testid="access-file-name" style={{ marginTop: 8, color: "var(--text-cloud)", fontSize: 13 }}>{file.name} · {(file.size / 1024).toFixed(0)} KB</div>}
            {fileError && <p data-testid="access-file-error" style={{ marginTop: 8, color: "#ff8a8a", fontSize: 13 }}>{fileError}</p>}
          </div>

          {err && <p data-testid="access-submit-error" style={{ marginTop: 14, color: "#ff8a8a", fontSize: 13 }}>{err}</p>}

          <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <button type="submit" disabled={!canSubmit} data-testid="access-submit" style={primaryButtonStyle(!canSubmit)}>
              {busy ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Loader2 size={14} className="animate-spin" /> Submitting…</span> : "Request access"}
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
}
